<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import StreamStatsService from '@/services/StreamStatsService'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import _ from "lodash"
import IconBase from '../icon/IconBase.vue'
import IconShowcase from '../icon/IconShowcase.vue'
import IconHandle from '../icon/IconHandle.vue'

// Props
const props = defineProps({
	stream: {
		type: Object,
		required: true,
	},
	isThumb: {
		type: Boolean,
		default: false,
	}
})

// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

// Refs
const thumbContainer = ref(null)
const hasStreamThumb = ref(null)
const thumbUrl = ref(null)
const scopeAlive = ref(true)
const thumbCreated = ref(false)

// Watch
watch(mediaPulse, () => {
	// overwrite url if load balancer enabled
	const hostId = mediaPulse.value && mediaPulse.value.hostId
	if (hostId) {
		const staticPrefix = mediaPulse.value.staticPrefix
		const anc = document.createElement('a')
		anc.href = thumbUrl.value
		if (/\w+-\d+/gi.test(hostId) || (props.stream && props.stream.pullUrl)) {
			anc.hostname = `${hostId}.castr.io`
			if (!anc.pathname.includes('static/') && staticPrefix) {
				anc.pathname = anc.pathname.replace(props.stream.key, `static/${mediaPulse.value.name}`)
			}
		}
		thumbUrl.value = anc.href

		if (!thumbCreated.value && thumbUrl.value) {
			setThumbnail()
		}
	}
})

// Lifecycle hooks
onMounted(() => {
	setMP()
})
// Methods
const setMP = async () => {
	if (props.stream && props.stream.key) {
		const isWowzaRegion = !!props.stream.region.v2
		let hostname = 'dvr.castr.io'
		let prefixPath = ''
		if (isWowzaRegion) {
			hostname = 'preview.castr.io'
			prefixPath = 'preview/'
		}

		thumbUrl.value = `https://${hostname}/`
		if (prefixPath) {
			thumbUrl.value += prefixPath
		}

		const regionDepId = props.stream.region.identifierHaxr
		thumbUrl.value += `${props.stream.key}/preview.mp4?&region=${regionDepId}`

		await setMediapulse(props.stream.key)
		subscribeMediaPulse(props.stream)
	}
}

const setMediapulse = async (streamKey) => {
	try {
		const pulseObject = await StreamStatsService.getStreamMediaPulse(streamKey)
		mediaPulse.value = pulseObject
	} catch (e) { }
}

const setThumbnail = () => {
	if (!scopeAlive.value) return

	const container = thumbContainer.value

	// prepare a new video element to overlap existing ones
	let mediaElement
	const streamEnabled = props.stream && props.stream.enabled
	const isStreamAlive = mediaPulse.value && mediaPulse.value.alive

	if (streamEnabled && isStreamAlive && container) {
		const isWowzaRegion = !!props.stream.region.v2
		if (isWowzaRegion) {
			const videoThumbUrl = getVideoThumbUrl()
			if (videoThumbUrl) {
				thumbCreated.value = true
				mediaElement = createImgInstance(videoThumbUrl, () => {
					container.appendChild(mediaElement)
				})
			}
		} else {
			const videoSrc = getVideoPreviewUrl()
			mediaElement = createVideoInstance(videoSrc)
			if (mediaElement) {
				thumbCreated.value = true
				container.appendChild(mediaElement)
			}
		}
	}

	// add few secs delay to make the video chunk appear
	if (mediaElement) {
		setTimeout(() => {
			mediaElement.className = mediaElement.className + ' ready'
			// reschedule thumb setup
			scheduleSetup(mediaElement.tagName.toLowerCase())
		}, 3000)
	} else {
		// reschedule thumb setup
		scheduleSetup()
	}
}

const scheduleSetup = (thumbType = 'video') => {
	const container = thumbContainer.value
	if (!container) return

	// remove old video thumbs
	const streamEnabled = props.stream.enabled

	// recheck if placeholder text has to appear instead of video thumb
	const isStreamThumb = streamEnabled && mediaPulse.value && mediaPulse.value.alive
	hasStreamThumb.value = isStreamThumb

	const mediaEls = container.getElementsByTagName(thumbType)
	Array.prototype.forEach.call(mediaEls, mediaEl => {
		// remove video if stream is offline or is older video
		if (!isStreamThumb) {
			if (container) {
				container.removeChild(mediaEl)
			}
		}
	})
}

const getVideoPreviewUrl = () => {
	if (!mediaPulse.value || !mediaPulse.value.alive) return
	const randomkey = Math.random().toString().slice(2)
	return thumbUrl.value + `&rand=${randomkey}`
}

// Cleanup on unmount
onUnmounted(() => {
	scopeAlive.value = false
	unsubscribeMediaPulse()
})

// Helper functions
const createVideoInstance = (videoSrc) => {
	if (!videoSrc) return
	const vid = document.createElement('video')
	vid.src = videoSrc
	vid.className = `${props.isThumb === true ? 'w-full max-h-[80px] lg:max-h-[100%]' : 'w-16'} bg-black rounded-lg aspect-video`
	return vid
}

const createImgInstance = (imgSrc, onLoad) => {
	if (!imgSrc) return
	const img = document.createElement('img')
	img.src = imgSrc
	img.className = `${props.isThumb === true ? 'w-full' : 'w-16'} bg-black rounded-lg aspect-video`
	if (onLoad) {
		img.onload = onLoad
	}
	return img
}
</script>

<template>
	<div class="flex gap-2 items-center" v-if="stream"> 
		<IconBase class="cursor-move shring-0" v-if="props.isThumb === false">
			<IconHandle />
		</IconBase>
		<div class="shrink-0 relative overflow-hidden absolute max-w-full left-0 top-0" ref="thumbContainer" v-if="mediaPulse?.alive">

		</div>
		<div v-else-if="props.isThumb === false" class="bg-surface-3 w-16 rounded-lg aspect-video flex items-center justify-center"
			:class="{ '': !stream.type, '': !hasStreamThumb }">
			<IconBase>
				<IconShowcase />
			</IconBase>
		</div>
	</div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
